
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Icons } from "@/components/icons";
import { UpcomingTasks } from "./components/UpcomingTasks";
import { RecentActivities } from "./components/RecentActivities";
import { ClientsSummary } from "./components/ClientsSummary";
import { QuickAccess } from "./components/QuickAccess";
import { AgencyStatusCard } from "./components/AgencyStatusCard";
import { OverdueTasksDialog } from "./components/OverdueTasksDialog";
import { useDashboardStats } from "@/hooks/use-dashboard-stats";
import { useOverdueTasks } from "@/hooks/use-overdue-tasks";
import { useSidebar } from "@/components/ui/sidebar";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/hooks/use-auth";

export default function Dashboard() {
  const { stats, isLoading } = useDashboardStats();
  const { tasks: overdueTasks, isLoading: isLoadingTasks } = useOverdueTasks();
  const { state } = useSidebar();
  const { user } = useAuth();
  const [overdueDialogOpen, setOverdueDialogOpen] = useState(false);
  const isCollapsed = state === "collapsed";

  return (
    <div className={`flex-1 space-y-4 px-1 md:px-4 py-4 md:py-8 transition-all duration-300 ${state === 'collapsed' ? 'md:ml-2' : ''}`}>
      <div className="flex items-center justify-between">
        {isCollapsed && (
          <div className="flex items-center">
            <BrandLogo agencyName={user?.agency_name} size="sm" />
          </div>
        )}
        <h2 className={`text-3xl font-bold tracking-tight ${isCollapsed ? 'ml-4' : ''}`}>Dashboard</h2>
        <div className="flex items-center space-x-2">
          {/* No dashboard actions for now */}
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total de Clientes
            </CardTitle>
            <Icons.users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-8 w-16 bg-gray-200 animate-pulse rounded" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats.clientsCount}</div>
                <p className="text-xs text-muted-foreground">
                  clientes ativos
                </p>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => setOverdueDialogOpen(true)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Tarefas Atrasadas
            </CardTitle>
            <Icons.alertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-8 w-16 bg-gray-200 animate-pulse rounded" />
            ) : (
              <>
                <div className="text-2xl font-bold text-red-600">{stats.overdueTasks}</div>
                <p className="text-xs text-muted-foreground">
                  tarefas em atraso
                </p>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Tarefas em Dia
            </CardTitle>
            <Icons.check className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-8 w-16 bg-gray-200 animate-pulse rounded" />
            ) : (
              <>
                <div className="text-2xl font-bold text-green-600">{stats.onTimeTasks}</div>
                <p className="text-xs text-muted-foreground">
                  tarefas pontuais
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <AgencyStatusCard 
          status={stats.agencyStatus}
          overdueTasks={stats.overdueTasks}
          onTimeTasks={stats.onTimeTasks}
          isLoading={isLoading}
        />
      </div>
      
      {/* Quick Access Section - Now in a separate card at the top of the content */}
      <Card>
        <CardHeader>
          <CardTitle>Acesso Rápido</CardTitle>
          <CardDescription>Links úteis para navegação</CardDescription>
        </CardHeader>
        <CardContent>
          <QuickAccess />
        </CardContent>
      </Card>
      
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card className="col-span-2">
              <CardHeader>
                <CardTitle>Tarefas Pendentes</CardTitle>
                <CardDescription>
                  Tarefas com data de entrega próxima
                </CardDescription>
              </CardHeader>
              <CardContent>
                <UpcomingTasks />
              </CardContent>
            </Card>
            <Card className="col-span-2">
              <CardHeader>
                <CardTitle>Atividades Recentes</CardTitle>
                <CardDescription>
                  Últimas ações realizadas
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RecentActivities />
              </CardContent>
            </Card>
            <Card className="col-span-2">
              <CardHeader>
                <CardTitle>Clientes</CardTitle>
                <CardDescription>
                  Resumo dos seus clientes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ClientsSummary />
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <OverdueTasksDialog
        open={overdueDialogOpen}
        onOpenChange={setOverdueDialogOpen}
        tasks={overdueTasks}
        isLoading={isLoadingTasks}
      />
    </div>
  );
}
